import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '@/features/auth';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { UserAvatar } from './UserAvatar';
import { SettingsIcon, LogOutIcon, InfoIcon, ChevronDownIcon, GlobeIcon, ShieldCheck, Home, XIcon } from 'lucide-react';
import { toast } from '@/components/CustomToast';
import { api } from '@/services/api';
import { useRTL } from '@/shared/hooks/useRTL';
import { useArcadeTransition } from '@/contexts/ArcadeTransitionContext';
import useMediaQuery, { MOBILE_BREAKPOINT } from '@/shared/hooks/useMediaQuery';
import { useLayout } from '@/contexts/LayoutContext';

// Chamama color palette
const COLORS = {
  yellow: '#F4D35E',
  lightBlue: '#CBEDFF',
  tan: '#DBC299',
  pink: '#FFB6F3',
  black: '#000000',
};

/**
 * UserMenu Component
 * Dropdown menu with user avatar, name, and action items
 * Uses Headless UI for accessibility
 */
export const UserMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('common');
  const { user, clearAuth, isAuthenticated } = useAuthStore();
  const { canAccessAdminSection } = usePermissions();
  const { triggerTransition } = useArcadeTransition();
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const isRTL = useRTL();
  const isInAdmin = location.pathname.startsWith('/admin');

  // Responsive: detect mobile viewport (<700px)
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const { sidebarCollapsed } = useLayout();
  const sidebarWidth = isMobile ? '0px' : sidebarCollapsed ? '5rem' : '16rem';
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const desktopBtnRef = useRef<HTMLDivElement>(null);

  const currentLanguage = i18n.language === 'he' ? t('common:languages.hebrew') : t('common:languages.english');

  // Safety net: navigate to login if session expires (transition handles normal logout)
  useEffect(() => {
    if (!isAuthenticated) {
      const timer = setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [isAuthenticated, navigate]);

  if (!user) return null;

  const handleLogout = () => {
    triggerTransition('/login', 'logout', () => {
      clearAuth();
    });
  };

  const handleLanguageChange = async () => {
    const newLanguage = i18n.language === 'he' ? 'en' : 'he';
    const newDir = newLanguage === 'he' ? 'rtl' : 'ltr';

    setIsChangingLanguage(true);

    try {
      // Update language in i18n
      await i18n.changeLanguage(newLanguage);

      // Update document direction
      document.documentElement.dir = newDir;
      document.documentElement.lang = newLanguage;

      // Persist to database
      await api.patch('/users/me/language', { preferredLanguage: newLanguage });
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsChangingLanguage(false);
    }
  };

  // Menu item component for mobile drawer
  const MobileMenuItem = ({
    icon: Icon,
    label,
    onClick,
    bgColor,
    disabled = false,
  }: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    onClick: () => void;
    bgColor: string;
    disabled?: boolean;
  }) => (
    <button
      onClick={() => {
        onClick();
        setIsMobileDrawerOpen(false);
      }}
      disabled={disabled}
      className="flex w-full items-center gap-4 px-4 py-4 text-base font-bold uppercase rounded-xl transition-all active:scale-[0.98]"
      style={{
        backgroundColor: bgColor,
        color: COLORS.black,
        border: `3px solid ${COLORS.black}`,
        boxShadow: `4px 4px 0px ${COLORS.black}`,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Icon className="h-6 w-6" />
      <span>{label}</span>
    </button>
  );

  return (
    <>
      {/* Desktop: Avatar button + Portal dropdown */}
      {!isMobile && (
        <div className="relative" ref={desktopBtnRef} data-testid="user-menu">
          <button
            type="button"
            onClick={() => setIsDesktopMenuOpen(!isDesktopMenuOpen)}
            className="typo-navigation neo-icon-btn flex items-center justify-center overflow-hidden rounded-full"
            style={{
              width: '56px',
              height: '56px',
              backgroundColor: 'var(--theme-surface)',
              color: 'var(--theme-text)',
              padding: 0,
            }}
          >
            <UserAvatar user={user} size="xl" className="!border-0" />
          </button>

          {/* Small Chevron Indicator */}
          <div
            className="neo-icon-btn absolute flex items-center justify-center pointer-events-none rounded-full"
            style={{
              bottom: '0px',
              ...(isRTL ? { left: '0px' } : { right: '0px' }),
              width: '18px',
              height: '18px',
              backgroundColor: COLORS.yellow,
            }}
          >
            <ChevronDownIcon className="h-3 w-3" style={{ color: COLORS.black }} />
          </div>
        </div>
      )}

      {/* Desktop Menu Dropdown - Portal anchored to avatar button */}
      {!isMobile && createPortal(
        <AnimatePresence>
          {isDesktopMenuOpen && (() => {
            const rect = desktopBtnRef.current?.getBoundingClientRect();
            if (!rect) return null;
            const dropdownStyle: React.CSSProperties = {
              top: rect.bottom + 8,
              right: window.innerWidth - rect.right,
            };
            return (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[100]"
                  onClick={() => setIsDesktopMenuOpen(false)}
                />
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                  className="fixed z-[101] w-56"
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    ...dropdownStyle,
                    backgroundColor: COLORS.yellow,
                    border: `3px solid ${COLORS.black}`,
                    boxShadow: `6px 6px 0px ${COLORS.black}`,
                  }}
                  role="menu"
                  aria-label={t('userMenu.settings')}
                >
                  {/* User Info Header */}
                  <div
                    className="px-4 py-3"
                    style={{
                      backgroundColor: COLORS.lightBlue,
                      borderBottom: `2px solid ${COLORS.black}`,
                    }}
                  >
                    <p className="text-sm font-bold uppercase" style={{ color: COLORS.black }}>
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs truncate mt-1" style={{ color: COLORS.black, opacity: 0.7 }}>
                      {user.email}
                    </p>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2 px-2">
                    {/* Language Toggle */}
                    <div className="flex justify-center px-4 py-2 mb-1">
                      <button
                        type="button"
                        dir="ltr"
                        onClick={handleLanguageChange}
                        disabled={isChangingLanguage}
                        className="relative flex items-center rounded-lg overflow-hidden w-full"
                        style={{
                          height: '34px',
                          backgroundColor: COLORS.tan,
                          border: `2px solid ${COLORS.black}`,
                          boxShadow: `2px 2px 0px ${COLORS.black}`,
                          opacity: isChangingLanguage ? 0.5 : 1,
                        }}
                        role="menuitem"
                        aria-label={t('common:a11y.toggleLanguage')}
                      >
                        <motion.div
                          className="absolute top-0 bottom-0 rounded-md"
                          style={{
                            width: '50%',
                            backgroundColor: COLORS.yellow,
                            border: `2px solid ${COLORS.black}`,
                            margin: '1px',
                          }}
                          animate={{
                            x: i18n.language === 'en' ? 0 : 'calc(100% - 2px)',
                          }}
                          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        />
                        <span
                          className="relative z-10 w-1/2 text-center text-sm font-bold"
                          style={{ color: COLORS.black, opacity: i18n.language === 'en' ? 1 : 0.4 }}
                        >
                          {t('common:languages.english')}
                        </span>
                        <span
                          className="relative z-10 w-1/2 text-center text-sm font-bold"
                          style={{ color: COLORS.black, opacity: i18n.language === 'he' ? 1 : 0.4 }}
                        >
                          {t('common:languages.hebrew')}
                        </span>
                      </button>
                    </div>

                    <button
                      onClick={() => { navigate('/settings'); setIsDesktopMenuOpen(false); }}
                      className="group flex w-full items-center gap-3 px-4 py-2 text-sm font-bold uppercase hover:translate-x-1 transition-all"
                      style={{ color: COLORS.black, marginBottom: '4px' }}
                      role="menuitem"
                    >
                      <SettingsIcon className="h-4 w-4" />
                      <span>{t('userMenu.settings')}</span>
                    </button>

                    {/* Switch to Admin / Switch to Home - Visible to OPERATOR+ */}
                    {canAccessAdminSection() && (
                      <button
                        onClick={() => { triggerTransition(isInAdmin ? '/bookings/my-bookings' : '/admin', isInAdmin ? 'home' : 'admin'); setIsDesktopMenuOpen(false); }}
                        className="group flex w-full items-center gap-3 px-4 py-2 text-sm font-bold uppercase hover:translate-x-1 transition-all"
                        style={{ color: COLORS.black, marginBottom: '4px' }}
                        role="menuitem"
                      >
                        {isInAdmin ? <Home className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                        <span>{isInAdmin ? t('navigation.switchToHome') : t('navigation.switchToAdmin')}</span>
                      </button>
                    )}

                    <button
                      onClick={() => { navigate('/about'); setIsDesktopMenuOpen(false); }}
                      className="group flex w-full items-center gap-3 px-4 py-2 text-sm font-bold uppercase hover:translate-x-1 transition-all"
                      style={{ color: COLORS.black }}
                      role="menuitem"
                    >
                      <InfoIcon className="h-4 w-4" />
                      <span>{t('userMenu.about')}</span>
                    </button>
                  </div>

                  {/* Logout Section */}
                  <div
                    className="py-2 px-2"
                    style={{ borderTop: `3px solid ${COLORS.black}` }}
                  >
                    <button
                      onClick={handleLogout}
                      data-testid="logout-button"
                      className="group flex w-full items-center gap-3 px-4 py-2 text-sm font-bold uppercase hover:translate-x-1 transition-all"
                      style={{
                        color: COLORS.black,
                        backgroundColor: COLORS.pink,
                        border: `2px solid ${COLORS.black}`,
                        boxShadow: `2px 2px 0px ${COLORS.black}`,
                      }}
                      role="menuitem"
                    >
                      <LogOutIcon className="h-4 w-4" />
                      <span>{t('userMenu.logout')}</span>
                    </button>
                  </div>
                </motion.div>
              </>
            );
          })()}
        </AnimatePresence>,
        document.body
      )}

      {/* Mobile: Button that opens drawer */}
      {isMobile && (
        <button
          type="button"
          onClick={() => setIsMobileDrawerOpen(true)}
          className="relative neo-icon-btn flex items-center justify-center overflow-hidden rounded-full"
          style={{
            width: '56px',
            height: '56px',
            backgroundColor: 'var(--theme-surface)',
            color: 'var(--theme-text)',
            padding: 0,
          }}
          data-testid="user-menu-mobile"
        >
          <UserAvatar user={user} size="xl" className="!border-0" />
        </button>
      )}

      {/* Mobile Full-Screen Drawer - Rendered via Portal to escape stacking context */}
      {isMobile && createPortal(
        <AnimatePresence>
          {isMobileDrawerOpen && (
            <>
              {/* Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100]"
                onClick={() => setIsMobileDrawerOpen(false)}
              />

            {/* Drawer - Slides from bottom with drag-to-dismiss */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              drag="y"
              dragConstraints={{ top: 0 }}
              dragElastic={0.2}
              onDragEnd={(_, info) => {
                // Swipe down to dismiss (if dragged more than 100px down)
                if (info.offset.y > 100) {
                  setIsMobileDrawerOpen(false);
                }
              }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-0 z-[101] flex flex-col overflow-hidden"
              dir={isRTL ? 'rtl' : 'ltr'}
              role="dialog"
              aria-modal="true"
              aria-label={t('common:a11y.userMenu')}
              style={{
                height: '100dvh',
                backgroundColor: COLORS.yellow,
              }}
            >
              {/* Drag Handle + Close Button Row */}
              <div
                className="flex items-center justify-between px-4 flex-shrink-0"
                style={{
                  paddingTop: 'env(safe-area-inset-top, 0.75rem)',
                  paddingBottom: '0.5rem',
                }}
              >
                {/* Drag Handle Indicator - Centered */}
                <div className="flex-1" />
                <div
                  className="w-12 h-1.5 rounded-full"
                  style={{ backgroundColor: COLORS.black, opacity: 0.3 }}
                />
                <div className="flex-1 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsMobileDrawerOpen(false)}
                    className="p-2 rounded-full transition-all"
                    style={{
                      backgroundColor: COLORS.tan,
                      border: `2px solid ${COLORS.black}`,
                      boxShadow: `2px 2px 0px ${COLORS.black}`,
                    }}
                    aria-label={t('common:a11y.closeMenu')}
                  >
                    <XIcon className="h-5 w-5" style={{ color: COLORS.black }} />
                  </button>
                </div>
              </div>

              {/* Profile Section - Centered, Large Avatar (2026 Best Practice) */}
              <div
                className="flex flex-col items-center px-4 pb-6 flex-shrink-0"
                style={{
                  backgroundColor: COLORS.yellow,
                }}
              >
                {/* Large Profile Avatar - Clean, no offset shadow */}
                <div className="mb-4">
                  <UserAvatar
                    user={user}
                    size="xl"
                    className="!w-24 !h-24 [&>img]:!w-24 [&>img]:!h-24 [&>img]:!shadow-none [&>img]:!border-4 [&>img]:!border-black [&>div]:!w-24 [&>div]:!h-24 [&>div]:!shadow-none [&>div]:!border-4 [&>div]:!border-black"
                  />
                </div>

                {/* User Name */}
                <h2
                  className="text-xl font-bold uppercase text-center"
                  style={{
                    color: COLORS.black,
                    fontFamily: 'var(--font-pixel), monospace',
                  }}
                >
                  {user.firstName} {user.lastName}
                </h2>

                {/* User Email */}
                <p
                  className="text-sm mt-1 text-center"
                  style={{ color: COLORS.black, opacity: 0.7 }}
                >
                  {user.email}
                </p>
              </div>

              {/* Divider */}
              <div
                className="mx-4"
                style={{
                  height: '4px',
                  backgroundColor: COLORS.black,
                }}
              />

              {/* Menu Items - Scrollable area */}
              <div
                className="flex-1 p-4 space-y-3 overflow-y-auto hide-scrollbar"
                style={{ overscrollBehaviorY: 'contain' }}
              >
                {/* Language Toggle - Full-width switch */}
                <button
                  type="button"
                  dir="ltr"
                  onClick={handleLanguageChange}
                  disabled={isChangingLanguage}
                  className="relative flex items-center rounded-xl overflow-hidden w-full"
                  style={{
                    height: '48px',
                    backgroundColor: COLORS.tan,
                    border: `3px solid ${COLORS.black}`,
                    boxShadow: `4px 4px 0px ${COLORS.black}`,
                    opacity: isChangingLanguage ? 0.6 : 1,
                  }}
                  aria-label={t('common:a11y.toggleLanguage')}
                >
                  <motion.div
                    className="absolute top-0 bottom-0 rounded-lg"
                    style={{
                      width: '50%',
                      backgroundColor: COLORS.yellow,
                      border: `2px solid ${COLORS.black}`,
                      margin: '2px',
                    }}
                    animate={{
                      x: i18n.language === 'en' ? 0 : 'calc(100% - 4px)',
                    }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                  <span
                    className="relative z-10 w-1/2 text-center text-base font-bold"
                    style={{ color: COLORS.black, opacity: i18n.language === 'en' ? 1 : 0.4 }}
                  >
                    {t('common:languages.english')}
                  </span>
                  <span
                    className="relative z-10 w-1/2 text-center text-base font-bold"
                    style={{ color: COLORS.black, opacity: i18n.language === 'he' ? 1 : 0.4 }}
                  >
                    {t('common:languages.hebrew')}
                  </span>
                </button>

                <MobileMenuItem
                  icon={SettingsIcon}
                  label={t('userMenu.settings')}
                  onClick={() => navigate('/settings')}
                  bgColor={COLORS.tan}
                />

                {canAccessAdminSection() && (
                  <MobileMenuItem
                    icon={isInAdmin ? Home : ShieldCheck}
                    label={isInAdmin ? t('navigation.switchToHome') : t('navigation.switchToAdmin')}
                    onClick={() => triggerTransition(isInAdmin ? '/bookings/my-bookings' : '/admin', isInAdmin ? 'home' : 'admin')}
                    bgColor="#FFA500"
                  />
                )}

                <MobileMenuItem
                  icon={InfoIcon}
                  label={t('userMenu.about')}
                  onClick={() => navigate('/about')}
                  bgColor="#90EE90"
                />
              </div>

              {/* Logout Section - Fixed at bottom */}
              <div
                className="flex-shrink-0 p-4"
                style={{
                  backgroundColor: COLORS.lightBlue,
                  borderTop: `4px solid ${COLORS.black}`,
                  paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))',
                }}
              >
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileDrawerOpen(false);
                  }}
                  data-testid="logout-button-mobile"
                  className="flex w-full items-center justify-center gap-4 px-4 py-4 text-base font-bold uppercase rounded-xl transition-all active:scale-[0.98]"
                  style={{
                    backgroundColor: COLORS.pink,
                    color: COLORS.black,
                    border: `3px solid ${COLORS.black}`,
                    boxShadow: `4px 4px 0px ${COLORS.black}`,
                  }}
                >
                  <LogOutIcon className="h-6 w-6" />
                  <span>{t('userMenu.logout')}</span>
                </button>
              </div>
            </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};
